<template>
  <div>
    <div
      v-if="historyList.length > 0 && tabList.length > 0"
      class="placeholder"
    />

    <div v-if="tabList.length > 0">
      <cityPanel
        :tabList="tabList"
        :showGroupName="false"
        @select="handleSelectCity"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { getHoteluxCityList } from "@/api/rest/common";
import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
// 引用当前目录下的子组件
import cityPanel from "@/packages/booking/hotel/Filter/LocationPopoverContent/components/cityPanel.vue";
import { ICity } from "@/packages/booking/hotel/Filter/LocationPopoverContent/types";

const { t, locale } = useI18n();

const emit = defineEmits(["select"]);

const YHotelStore = useHotelStoreHook();
const { historyList } = storeToRefs(YHotelStore);
const yChannelStore = useYChannelStoreHook();

const originCityList = ref<ICity[]>([]);
const isLoading = ref(true);

const hotCityList = computed(() => {
  return originCityList.value.map(mapCityByLang);
});

// 使用 configs 中的 tabList 配置
const tabList = computed(() => {
  const configTabList = yChannelStore.configs?.pages?.locationPopover?.tabList;
  const list = hotCityList.value;

  if (configTabList && configTabList.length > 0) {
    // 如果配置了 tabList，需要处理数据，将 city 数据填充到 groups 中
    return configTabList.map(tab => ({
      name: t(tab.name),
      groups: tab.groups.map(group => ({
        label: t(group.label),
        list: group.list ? group.list : list // 如果没有指定 list，使用所有城市
      }))
    }));
  }

  // 默认配置
  return [
    {
      name: t("shared.common.hotDestinationsTitle"),
      groups: [
        {
          label: t("shared.common.hotCity"),
          list: filterCityList(list, ["中国台湾"], false)
        }
      ]
    }
  ];
});

const filterCityList = (
  list: ICity[],
  filterCountry: string[],
  includes: Boolean = true
) => {
  return list.filter(item =>
    includes
      ? filterCountry.includes(item.country)
      : !filterCountry.includes(item.country)
  );
};

const handleSelect = item => {
  emit("select", item);
};

const handleSelectCity = item => {
  handleSelect(item);
};

const mapCityByLang = (city: ICity) => {
  const lang = locale.value;
  if (lang === "zh") {
    return {
      ...city,
      name: city.city,
      countryName: city.country,
      provinceName: city.province
    };
  }

  const t = city.translation?.[lang] || city.translation?.en;
  return {
    ...city,
    name: t?.name || city.city,
    countryName: t?.country || city.country,
    provinceName: t?.province || city.province
  };
};

onMounted(async () => {
  isLoading.value = true;
  try {
    const [_, cityData] = await Promise.all([
      YHotelStore.getSearchHistory(),
      getHoteluxCityList()
    ]);
    originCityList.value = cityData?.hotCities || [];
  } catch (error) {
    console.error("Failed to load discovery data:", error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style lang="scss" scoped>
.placeholder {
  width: 100%;
  height: 10px;
  background: #fafafa;
}
</style>
